import en_US from '../_generated/lang/en_US.json'
import da_DK from '../_generated/lang/da_DK.json'
import { GenericStorage } from './dom/storage'
import ApiModel from '../models/ApiModel'

const REPLACEMENT_PLACEHOLDER = '{}'
const prepare = (
    subject: string,
    replacements: string[],
    placeholder: string = REPLACEMENT_PLACEHOLDER
) => {
    for (let i = 0; i < replacements.length; i++)
        subject = subject.replaceAll(`{${i}}`, replacements[i])
    return subject
}

// NOTE: Use the type of en_US keys, assuming it will be the most complete language.
declare type TString = keyof typeof en_US
declare type Translation = Record<TString, string>
declare type Language = 'en' | 'da'

const localization: Record<Language, Translation> = {
    en: en_US,
    da: da_DK,
}

const languageStorage = new GenericStorage<Language>('USER_LANGUAGE')
const inferUserLanguage = (): Language => {
    // Attempt to read and parse the browser language through the navigator or assume English.
    const userLanguage = navigator.language?.replace(/-\w{2}$/, '') as Language | undefined
    return userLanguage && userLanguage in localization ? userLanguage : 'en'
}

// Because language switching requires a page reload, we query it on startup and never look it up again.
const language: Language = languageStorage.read() ?? inferUserLanguage()
export const getLanguage = () => language
export const setLanguage = (lang: Language) => {
    languageStorage.store(lang)
    location.reload()
}

/**
 * Translate
 *
 * This function takes a translation key and returns the language string for the current language.
 * The translation mechanism works entirely outside of React and can always be called statically.
 * This is because otherwise, mostly all React components would have to call a useT() hook or similar
 *  in order to fetch language strings, which would result in more verbose and repetetive component code.
 * Because switching language is a quite rare occurrence, we accept a page reload to do so.
 */
export default function t(key: TString, ...replacements: string[]): string {
    const lang = getLanguage()
    let string = localization[lang]?.[key] ?? ''
    if (replacements) string = prepare(string, replacements)
    return string
}

/**
 * Dynamic Translate
 *
 * Use this unsafe version of the t function to supply a non-literal string.
 * This is useful for translating codes from the API which are delivered as a string in an http response body.
 */
export const _t = (key: string, ...replacements: string[]) =>
    t(key.toLowerCase() as TString, ...replacements)

/**
 * Translate Field
 *
 * Use this function to translate a value for a model field into a prettier label.
 * The key is resolved as `model_field_value` and is always lower case.
 * For instance, the `type` field of a `DataModel` class might contain the value "default"
 *  which could be translated as "Standard" in English.
 * In this case, the resulting key would be "datamodel_type_default" and return "Standard" for `en` language.
 *
 * NOTE: Strings translated in this manner do not currently support replacements.
 *
 * @example _tf('ModelClass', 'field', model.field) -> 'modelclass_field_value'
 */
export const _tf = (model: string, field: string, value?: string) => {
    const parts = [model, field, value].filter((s) => !!s).map((s) => s?.toLowerCase())
    const key = parts.join('_')
    return _t(key)
}

/**
 * Translate ApiModel
 *
 * Shorthand for translating fields of an ApiModel instead of having to use the more verbose _tf manually.
 * According to TypeScript all is fine, but providing non-string fields names will result in missing or incorrect translations.
 *
 * NOTE: Using `any` because these types are not used here, but are required by TypeScript.
 *
 * @example _ta(model, 'field') -> model_field_value
 * @example _ta(model, 'field', 'defined') -> model_field_defined
 */
export const _ta = <T extends ApiModel<any>>(model: T, field: keyof T, value?: any) => {
    // Despite this cast only string values can be used as translation keys.
    value = value || (model[field] as any)
    return _tf(model.Class, field.toString(), value?.toString())
}
